import { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Grid, Alert, CircularProgress, Typography, Box,
} from '@mui/material';
import type { Study } from '../../api/types';
import { api } from '../../api/rpc';

interface UploadRequestDialogProps {
  open: boolean;
  study: Study | null;
  onClose: () => void;
  onComplete: () => void;
}

export const UploadRequestDialog = ({ open, study, onClose, onComplete }: UploadRequestDialogProps) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [to, setTo] = useState('');
  const [message, setMessage] = useState('');
  const [reason, setReason] = useState('');

  const handleSubmit = async () => {
    if (!to.trim()) {
      setError('Recipient is required');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await api.uploadRequest({
        studyKey: study?.objKey,
        to: to.trim(),
        message: message || undefined,
        reason: reason || undefined,
      });
      onComplete();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create upload request');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Upload Request</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {study && (
          <Box sx={{ mb: 2, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Typography variant="body2">
              <strong>{study.name}</strong> — {study.acc} — {study.mod}
            </Typography>
          </Box>
        )}
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid size={{ xs: 12 }}>
            <TextField
              label="Recipient"
              value={to}
              onChange={e => setTo(e.target.value)}
              fullWidth size="small"
              required
              placeholder="User or email address"
              autoFocus
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              label="Reason"
              value={reason}
              onChange={e => setReason(e.target.value)}
              fullWidth size="small"
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              label="Message"
              value={message}
              onChange={e => setMessage(e.target.value)}
              fullWidth multiline rows={3} size="small"
              placeholder="Optional message to include with the request"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={saving}>
          {saving ? <CircularProgress size={20} /> : 'Send Request'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
